import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { Story, Hostage } from "../types";
import "../../style/StoryCard.css";
import { useTranslation } from "react-i18next";
import { Share, Delete, Edit, PushPin, PushPinOutlined } from "@mui/icons-material";
import { useUser } from "../../context/UserContext";
import StoryForm from "./StoryForm";
import heartUnselectedIcon from "../../icons/heart-unselected.svg";
import heartSelectedIcon from "../../icons/heart-selected.svg";
import Linkify from "react-linkify";
import { HostagePageLink } from "../HostagePageLink";
import VideoThumbnail from "./VideoThumbnail";

type Props = {
  story: Story;
  hostage: Hostage;
  onRefresh?: () => void;
  onDeleteStory?: (id: string | undefined) => void;
};

const StoryCard = ({ story, hostage, onRefresh, onDeleteStory }: Props) => {
  const { t, i18n } = useTranslation();
  const { user } = useUser();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isAddStoryDrawerOpen, setIsAddStoryDrawerOpen] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isEmojiSelected, setIsEmojiSelected] = useState(story.reactionsSummary?.isSelected);
  const [reactionCount, setReactionCount] = useState<number>(story.reactionsSummary?.reactionsCount ?? 0);

  const maxTextLength = 130;
  const handleToggleReadMore = () => {
    setIsDescriptionExpanded(!isDescriptionExpanded);
  };

  const storyDescription = story[i18n.language as "en" | "he"]?.description ?? story.description;
  const hostageName = story[i18n.language as "en" | "he"]?.hostageName;

  const truncatedText =
    storyDescription.length > maxTextLength && !isDescriptionExpanded
      ? `${storyDescription.substring(0, maxTextLength)}...`
      : storyDescription;

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          text: t("eventCard.shareText", { storyName: storyDescription }),
          url: window.location.href,
        });
      } catch (err) {
        console.error("Error sharing:", err);
      }
    }
  };

  function canEdit() {
    return (
      user?.role === "admin" ||
      (user?.role === "moderator" && story.hostage && user?.moderating?.includes(story.hostage))
    );
  }

  const confirmDelete = () => {
    fetch(`/admin/story/${story.id}`, {
      method: "DELETE",
      credentials: "include",
      headers: {
        "X-HOSTAGE-ID": story.hostage,
      },
    })
      .catch((err) => {
        console.error("Error deleting event:", err);
      })
      .finally(() => {
        setIsDeleteDialogOpen(false); // Close the dialog after deletion
        if (onDeleteStory) {
          onDeleteStory(story.id);
        }
        if (onRefresh) {
          onRefresh();
        }
      });
  };

  function handleEditStory(): void {
    setIsAddStoryDrawerOpen(true);
  }

  async function handlePinStory(isPinned: boolean) {
    const storyData: Story = {
      ...story,
      //hostage: story.hostage,
      //description: storyDescription,
      //photo: story.photo,
      isPinned: isPinned,
    };

    try {
      await fetch(`/admin/story/${story.id}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-HOSTAGE-ID": storyData.hostage,
        },
        body: JSON.stringify(storyData),
      });
      window.location.reload();
    } catch (err) {
      console.error("Error updating story:", err);
    }
  }

  function getShareTimeString(): string {
    if (!story.createdAt) {
      return "";
    }
    const createdTime = new Date(story.createdAt);
    const now = new Date();
    const diffInMs = now.getTime() - createdTime.getTime();

    const diffInSeconds = Math.floor(diffInMs / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    const diffInWeeks = Math.floor(diffInDays / 7);

    if (diffInMinutes < 1) {
      return t("story.timeAgo.justNow");
    } else if (diffInMinutes < 60) {
      return t("story.timeAgo.minutesAgo", { count: diffInMinutes });
    } else if (diffInHours < 24) {
      return t("story.timeAgo.hoursAgo", { count: diffInHours });
    } else if (diffInDays < 7) {
      return t("story.timeAgo.daysAgo", { count: diffInDays });
    } else {
      return t("story.timeAgo.weeksAgo", { count: diffInWeeks });
    }
  }

  const handleReactionToggle = async () => {

    const action = isEmojiSelected ? "unselected" : "selected";

    if(action === "selected")
    {
      setReactionCount((prevCount) => (prevCount + 1));
      setIsEmojiSelected(true);
    } 
    else{
      setIsEmojiSelected(false);
      setReactionCount((prevCount) => (prevCount - 1));
    }

    try {
        const response = await fetch('/api/emoji', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json'},
            body: JSON.stringify({
                storyId: story.id,
                action
            })
        })

        const data = await response.json();

        if(response.ok){
            setReactionCount(data.reactionsCount); 
        }
        else{
            console.error("Faild to updated reaction:", data.message)
        }

    } catch (error){
        console.error("Error while sending emoji:", error)
    }
}

  return (
    <Card className="story-container">
      <Box display="flex" alignItems="center" sx={{ padding: "8px" }}>
        <img
          src={hostage.profilePicture}
          alt="user"
          style={{ width: "40px", height: "40px", borderRadius: "50%", objectFit: "cover" }}
        />
        <Box display="flex" flexDirection="column" textAlign="left" paddingRight="10px">
          <Typography variant="body2">{hostage[i18n.language as "en" | "he"].name_with_title}</Typography>
          <Typography variant="body2" style={{ lineHeight: "1", color: "#727272" }}>
            {getShareTimeString()}
          </Typography>
        </Box>
      </Box>
      <CardContent className="story-card-content">
        <Typography variant="body2" align="right">
          <Linkify>{truncatedText}</Linkify>
        </Typography>
        {storyDescription.length > maxTextLength && (
          <Button onClick={handleToggleReadMore} className="read-more">
            {isDescriptionExpanded ? t("story.readLess") : t("story.readMore")}
          </Button>
        )}
      </CardContent>
      {story.photo && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: 0,
            padding: 0,
          }}
        >
          {story.photo.includes("images/") ? (
            <img
              src={story.photo}
              alt="story"
              style={{
                width: "100%",
                height: "auto",
                display: "block",
              }}
            />
          ) : (
            <VideoThumbnail videoSrc={story.photo} thumbnailTime={5} />
          )}
        </Box>
      )}

      <Box display="flex" justifyContent="space-between" mt={2}>
        <Box display="flex" alignItems="center" mr={1}>
          <IconButton onClick={handleReactionToggle}>
            {isEmojiSelected ? 
            <img src={heartSelectedIcon} alt="users count" /> :
            <img src={heartUnselectedIcon} alt="users count" />}
          </IconButton>
          <Box ml={0.5}>{reactionCount}</Box>
        </Box>
        <Box display="flex" alignItems="center">
          <IconButton onClick={handleShare}>
            <Share />
          </IconButton>
          {canEdit() && story.isPinned && (
            <IconButton aria-label="unpin" onClick={async () => await handlePinStory(false)}>
              <PushPin />
            </IconButton>
          )}
          {canEdit() && !story.isPinned && (
            <IconButton aria-label="pin" onClick={async () => await handlePinStory(true)}>
              <PushPinOutlined />
            </IconButton>
          )}
          {canEdit() && (
            <IconButton aria-label="edit" onClick={() => handleEditStory()}>
              <Edit />
            </IconButton>
          )}
          {canEdit() && (
            <IconButton aria-label="delete" onClick={() => setIsDeleteDialogOpen(true)}>
              <Delete />
            </IconButton>
          )}
        </Box>
      </Box>
      <StoryForm
        open={isAddStoryDrawerOpen}
        onClose={() => {
          setIsAddStoryDrawerOpen(false);
          window.location.reload();
        }}
        hostageId={story.hostage}
        story={story}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{t("story.deleteConfirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t("story.deleteConfirmText")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)} color="primary">
            {t("story.cancelBtn")}
          </Button>
          <Button onClick={confirmDelete} color="secondary" autoFocus>
            {t("story.confirmBtn")}
          </Button>
        </DialogActions>
      </Dialog>
      {hostageName && story.hostage ? (
        <HostagePageLink hostageId={story.hostage} hostageName={hostageName} />
      ) : null}
    </Card>
  );
};

export default StoryCard;
